import * as React from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { SearchBar } from './SearchBar';
import Progress from '../shared/Progress';
import SearchTable from './SearchTable';
import { batch, useDispatch, useSelector } from 'react-redux';
import { getRecentlyViewed, emailActiveSearch } from '../../../api/emailsApi/emails';
import { changeAlertText } from '../../actions/index';
import { getLocalStorageWithExpiry, setLocalStorageWithExpiry, THIRTY_MINUTES_INT } from '../../utils/browser';
import * as MailActions from '../../actions/mail';
import SearchSubmit from './SearchSubmit';
import AssignWithAce from '../docAssistant/AssignWithAce';
import { DocAssistantCaptureContext, CAPTURE_STATUS } from '../docAssistant/DocAssistantCaptureContext';
import Skeleton from '../shared/Skeletons/Skeleton';
import Svg from '../shared/Svg';
import colors from '../../utils/colors';

import './SearchContainer.scss';

export const SearchContext = createContext({});
const { capitalCase } = require('change-case');

const debounce = (cb, delay) => () => {
  const timeout = setTimeout(cb, delay);
  return () => clearTimeout(timeout);
};

/**
 * Container for the search bar and search results table.
 * @param {boolean} showBack - Whether to show the back button.
 * @return {JSX.Element}
 */
export const SearchContainer = ({ showBack }) => {
  const dispatch = useDispatch();
  const [searchResultsLoading, changeSearchResultsLoadingState] = useState(false);
  // The capture poll lives in SaveToContainer; show the ACE skeleton while it is still searching.
  const { status: aceStatus } = useContext(DocAssistantCaptureContext);
  const aceSearching = aceStatus === CAPTURE_STATUS.SEARCHING;
  const objectDefaults = useSelector((state) => state.mail.settings?.objectPickerDefaults);
  const salesforceProfileId = useSelector((state) => state.auth.user?.salesforceProfileId);

  const getDefaultSearchObject = (state) => {
    const objectsAndDisplayFields = state.mail.settings?.objectsAndDisplayFields;
    const defaultObject = salesforceProfileId && objectDefaults && objectDefaults[salesforceProfileId];
    if (defaultObject) {
      const filteredDefaultObjects = objectsAndDisplayFields.filter((object) => object.apiName === defaultObject.apiName);
      return filteredDefaultObjects[0];
    } else {
      return objectsAndDisplayFields[0];
    }
  };

  const formatPassiveSearch = (recentlyViewedRecords, listViewRecords, viewName) => {
    const newRecords = {};
    newRecords['Recently Viewed'] = recentlyViewedRecords;
    if (listViewRecords?.length && viewName) {
      newRecords[viewName] = listViewRecords;
    }
    return newRecords;
  };

  // Track the current and prior search object to detect a change.
  const defaultSearchObject = useSelector((state) => getDefaultSearchObject(state));
  const recentRecords = useSelector((state) => state.mail.recentRecords);
  const selectedRecords = useSelector((state) => state.mail.selectedRecords);
  const listView = useSelector((state) => state.mail.listViews);
  const [searchObject, updateSearchObject] = useState(defaultSearchObject);
  const [records, updateSearchResults] = useState();
  const [searchQuery, updateSearchQuery] = useState('');
  const [checkedRecords, setCheckedRecords] = useState(selectedRecords);

  const updateSearchObjectCallback = (apiName) => {
    updateSearchObject(apiName);
    updateSearchQuery('');
  };


  const validateResponse = (response, source) => {
    if (response instanceof Error) {
      const message = source === 'search' ? 'Error searching. Please retry.' : 'Error loading';
      updateSearchObjectCallback(defaultSearchObject);
      batch(() => {
        dispatch(changeAlertText(response.response?.data?.Message || response.data?.Message || message, 'error'));
      });
      return formatPassiveSearch(recentRecords, listView, defaultSearchObject?.searchView?.Name);
    }
  };

  const updateRecordsTable = async () => {
    changeSearchResultsLoadingState(true);

    let newRecords = {};
    // If there is a query, perform an active search
    if (searchQuery) {
      const response = await emailActiveSearch(searchObject.apiName, searchQuery);
      const defaultRecords = validateResponse(response, 'search');
      if (defaultRecords) {
        newRecords = defaultRecords;
      } else {
        const defaultLabel = `My ${searchObject.pluralLabel}`;
        // If no list view selected, searchView will not be set. If set to "None", it will be an empty object
        if (response.data?.ListViewRecords?.length && searchObject?.searchView && Object.keys(searchObject.searchView).length > 0) {
          const listViewTabName = searchObject.searchView?.Name || defaultLabel;
          newRecords[listViewTabName] = response.data.ListViewRecords;
        } else if (response.data?.ListViewRecords?.length) {
          newRecords[defaultLabel] = response.data.ListViewRecords;
        }

        const allRecordsTabName = `All ${pluralize(capitalCase(searchObject.label))}`;
        newRecords[allRecordsTabName] = response.data?.AllRecords;
      }
    } else if (defaultSearchObject.apiName != searchObject.apiName) {
      // if there is no query, we are not using the default object, use passive search for new object
      let response = {};
      const cacheKey = `litifyobj#${searchObject.apiName}`;
      const cachedObjectDetails = getLocalStorageWithExpiry(cacheKey);
      if (!cachedObjectDetails) {
        response = await getRecentlyViewed(searchObject.apiName);
        const toBeCached = {
          AllRecords: response?.data?.AllRecords || [],
          ListViewRecords: response?.data?.ListViewRecords || [],
        };
        setLocalStorageWithExpiry(cacheKey, toBeCached, THIRTY_MINUTES_INT);
      } else {
        response.data = cachedObjectDetails; // set response.data from cache
      }
      newRecords = validateResponse(response) || formatPassiveSearch(response.data?.AllRecords, response.data?.ListViewRecords, searchObject?.searchView?.Name);
    } else {
      // if there is no query and we are implicitly using the default object, render recently viewed and any list views from state.
      newRecords = formatPassiveSearch(recentRecords, listView, defaultSearchObject?.searchView?.Name);
    }

    updateSearchResults(newRecords);
    changeSearchResultsLoadingState(false);
  };

  useEffect(() => {
    updateRecordsTable();
  }, [searchObject, recentRecords]);

  const debounceEffect = () => {
    if (![1, 2].includes(searchQuery.length)) {
      updateRecordsTable();
    }
  };

  useEffect(debounce(() => {
    debounceEffect();
  }, 700), [searchQuery]);

  useEffect(() => {
    if (![1, 2].includes(searchQuery.length)) {
      changeSearchResultsLoadingState(true);
    }
  }, [searchQuery]);

  const handleBackButtonClick = () => {
    dispatch(MailActions.setIsSearchingRelatedRecords(false));
  };

  // If there is a search query, generate multiple tabs determined by header texts.
  return (
    <div id='search-container'>
      <SearchContext.Provider value={{
        searchObject,
        searchQuery,
        searchObjectUpdateCallback: updateSearchObjectCallback,
        searchQueryCallback: updateSearchQuery,
        checkedRecords,
        setCheckedRecords,
      }} >
        { showBack && <div className='back-container'>
          <button
            className='back'
            onClick={handleBackButtonClick}
          >
            <Svg
              className='back-icon'
              data='/assets/back.svg'
              fill={colors.strongBlue}
              onClick={handleBackButtonClick}
            />
          Back
          </button>
        </div>}
        <SearchSubmit />
        <SearchBar />
        { aceSearching ? (
          <div className='ace-searching' data-testid='da-searching-state'>
            <div className='ace-searching-label'>
              <Svg className='ace-searching-icon' data='/assets/folderSearch.svg' />
              Searching matters with ACE
            </div>
            {[0, 1, 2].map((i) => (
              <div key={i} className='ace-searching-row'>
                <Skeleton className='ace-skeleton' height='24px' width='24px' margin='0' />
                <Skeleton className='ace-skeleton ace-skeleton-bar' height='10px' width='55%' margin='0' />
              </div>
            ))}
          </div>
        ) : (
          <>
            { searchResultsLoading && <Progress /> }
            <SearchTable
              tableInfo={records}
              isSearching={searchResultsLoading}
            />
          </>
        )}
        <AssignWithAce />
      </SearchContext.Provider>
    </div>
  );
};

SearchContainer.propTypes = {
  showBack: PropTypes.bool,
};
